import { Box, Flex, Menu, NavLink, Stack, rem } from '@mantine/core';
import P1Medium from 'components/typography/P1Medium';
import {
  DashboardNavigationGroupKey,
  DashboardNavigationSection,
  getDashboardActiveSection,
  useDashboardNavigation,
} from 'layouts/dashboard/navigation';
import NavbarItem from 'layouts/dashboard/navbar/NavbarItem';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface NavbarMenuGroupProps {
  menuKey: DashboardNavigationGroupKey;
  label: string;
  icon: ReactNode;
  isCollapsed: boolean;
  isActive: boolean;
  openedMenu: DashboardNavigationGroupKey | null;
  setOpenedMenu: (menu: DashboardNavigationGroupKey | null) => void;
  children: ReactNode;
}

/**
 * Renders a grouped navbar section with iPad-safe collapsed interaction.
 */
function NavbarMenuGroup({
  menuKey,
  label,
  icon,
  isCollapsed,
  isActive,
  openedMenu,
  setOpenedMenu,
  children,
}: NavbarMenuGroupProps) {
  const opened = openedMenu === menuKey;

  if (isCollapsed) {
    return (
      <Menu
        opened={opened}
        onChange={(nextOpened) => setOpenedMenu(nextOpened ? menuKey : null)}
        withinPortal
        position="right-start"
        offset={12}
        width={280}
      >
        <Menu.Target>
          <div>
            <NavLink
              active={isActive || opened}
              icon={icon}
              label={label}
              className="collapsed parentCollapsed"
              style={{ paddingTop: '32px' }}
            />
          </div>
        </Menu.Target>

        <Menu.Dropdown
          p={0}
          style={{
            borderRadius: '16px',
            boxShadow: '0px 12px 28px 0px rgba(25, 25, 25, 0.08)',
            border: '1px solid #E8E8E8',
          }}
        >
          <Box
            px={24}
            py={12}
            sx={{
              '& .collapsed': {
                paddingLeft: rem(8),
                gap: rem(16),
              },
              '& button': {
                paddingTop: rem(8),
                paddingBottom: rem(8),
                paddingLeft: rem(30),
              },
            }}
          >
            <P1Medium color="gray.9" style={{ marginBottom: '12px' }}>
              {label}
            </P1Medium>
            {children}
          </Box>
        </Menu.Dropdown>
      </Menu>
    );
  }

  return (
    <NavLink
      opened={opened}
      onChange={(nextOpened) => setOpenedMenu(nextOpened ? menuKey : null)}
      icon={icon}
      label={label}
    >
      {children}
    </NavLink>
  );
}

/**
 * Renders the items of a grouped navbar section.
 */
function NavbarSectionChildren({
  section,
  isCollapsed,
}: {
  section: DashboardNavigationSection & { key: DashboardNavigationGroupKey };
  isCollapsed: boolean;
}) {
  return (
    <Flex>
      <div
        style={
          isCollapsed
            ? { width: '1px', marginLeft: '12px', background: '#CFCFCF' }
            : { width: '1px', marginLeft: '24px', background: '#CFCFCF' }
        }
      />
      <Stack spacing={4} sx={{ '&:empty': { display: 'none' } }}>
        {section.items.map((item) => (
          <NavbarItem
            key={item.to}
            to={item.to}
            label={item.label}
            icon={item.icon}
            alwaysShowLabel
            isCollapsed={isCollapsed}
          />
        ))}
      </Stack>
    </Flex>
  );
}

/**
 * The navigation menu inside the navbar.
 */
export default function NavbarMenu({ isCollapsed }: { isCollapsed: boolean }) {
  const location = useLocation();
  const sections = useDashboardNavigation();
  const activeMenu = useMemo(() => getDashboardActiveSection(location.pathname), [location.pathname]);

  const [openedMenu, setOpenedMenu] = useState<DashboardNavigationGroupKey | null>(() => {
    return isCollapsed || activeMenu === 'dashboard' || activeMenu === null ? null : activeMenu;
  });

  useEffect(() => {
    if (isCollapsed) {
      return;
    }

    if (activeMenu && activeMenu !== 'dashboard') {
      setOpenedMenu((currentMenu) => (currentMenu === activeMenu ? currentMenu : activeMenu));
    }
  }, [activeMenu, isCollapsed]);

  return (
    <Stack
      spacing={12}
      align={isCollapsed ? 'flex-start' : 'initial'}
      sx={{
        width: isCollapsed ? 'fit-content' : 'initial',
        marginLeft: isCollapsed ? 'auto' : 'inherit',
        marginRight: isCollapsed ? 'auto' : 'inherit',
        gap: isCollapsed ? '0px' : '16px',
      }}
    >
      {sections.map((section) => {
        if (section.key === 'dashboard' && section.to) {
          return (
            <NavbarItem
              key={section.key}
              to={section.to}
              icon={section.icon}
              label={section.label}
              isCollapsed={isCollapsed}
            />
          );
        }

        const groupedSection = section as DashboardNavigationSection & { key: DashboardNavigationGroupKey };

        return (
          <NavbarMenuGroup
            key={groupedSection.key}
            menuKey={groupedSection.key}
            label={groupedSection.label}
            icon={groupedSection.icon}
            isCollapsed={isCollapsed}
            isActive={activeMenu === groupedSection.key}
            openedMenu={openedMenu}
            setOpenedMenu={setOpenedMenu}
          >
            <NavbarSectionChildren section={groupedSection} isCollapsed={isCollapsed} />
          </NavbarMenuGroup>
        );
      })}
    </Stack>
  );
}
